import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Checkbox Component
 *
 * A control that allows the user to toggle between checked and not checked.
 * Built on Radix UI Checkbox primitive following shadcn/ui design standards.
 *
 * @example
 * ```tsx
 * // Basic checkbox
 * <Checkbox id="terms" />
 * <Label htmlFor="terms">Accept terms and conditions</Label>
 *
 * // Controlled checkbox
 * const [checked, setChecked] = useState(false);
 * <Checkbox checked={checked} onCheckedChange={setChecked} />
 *
 * // With form integration
 * <Checkbox name="newsletter" value="yes" />
 *
 * // Disabled state
 * <Checkbox disabled />
 *
 * // With custom styling
 * <Checkbox className="border-red-500" />
 * ```
 */
const Checkbox = React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
    <CheckboxPrimitive.Root
        ref={ref}
        data-slot="checkbox"
        className={cn(
            "setu:peer setu:!border-input setu:bg-background setu:data-[state=checked]:bg-primary setu:data-[state=checked]:text-primary-foreground dark:setu:data-[state=checked]:bg-primary setu:data-[state=checked]:!border-primary setu:focus-visible:!border-ring setu:focus-visible:ring-ring/50 setu:aria-invalid:ring-destructive/20 setu:dark:aria-invalid:ring-destructive/40 setu:aria-invalid:!border-destructive setu:size-4 setu:shrink-0 setu:rounded-[4px] setu:!border setu:shadow-xs setu:transition-shadow setu:outline-none setu:focus-visible:ring-[3px] setu:disabled:cursor-not-allowed setu:disabled:opacity-50",
            className
        )}
        {...props}
    >
        <CheckboxPrimitive.Indicator
            data-slot="checkbox-indicator"
            className="setu:flex setu:items-center setu:justify-center setu:text-current setu:transition-none"
        >
            <CheckIcon className="setu:size-3.5" />
        </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
